import { Card, CardHeader, CardContent, Avatar } from "@mui/material";
import { useCurrentUser } from "../../../../App/Queries/UserQueries";
import { ICandidateNote } from "../../../../App/Typings/CandidateProfile";
import { IUser } from "../../../../App/Typings/User";
import { NoteDisplay } from "./NoteDisplay";

export default function Note({ note }: { note: ICandidateNote }) {
  return (
    <Card sx={{ mb: 2, position: "relative" }}>
      {note.id === "" ? (
        <CardContent>Uploading note...</CardContent>
      ) : (
        <>
          <CustomHeader note={note} />
          <CardContent sx={{ pl: "62px", pt: 0 }}>
            <NoteDisplay note={note} />
          </CardContent>
        </>
      )}
    </Card>
  );
}

const CustomHeader = ({ note }: { note: ICandidateNote }): JSX.Element => {
  const user = useCurrentUser();
  const title = getNoteCardHeader(note);
  const subheader = getNoteCardSubHeader(note);
  return (
    <CardHeader
      title={title}
      subheader={subheader}
      data-test={`note-${note.id}-header`}
      data-date={note.createDate}
      avatar={<UserAvatar user={user} />}
      disableTypography={false}
      sx={{
        ".MuiCardHeader-content": {
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          gap: { xs: "0", md: "1.5ch" },
          flexDirection: { xs: "column", md: "row" },
          pr: "50px",
        },
        ".MuiCardHeader-title": {
          fontWeight: "bold",
        },
        ".MuiCardHeader-subheader": {
          fontSize: "0.8em",
        },
      }}
    />
  );
};

const UserAvatar = ({ user }: { user: IUser }): JSX.Element => (
  <Avatar
    data-test={"note-user-avatar"}
    src={user?.picture}
    sx={{
      color: "text.white",
      textTransform: "uppercase",
      fontWeight: "bold",
      width: 30,
      height: 30,
      backgroundColor: "rgba(0,0,0,0.2)",
    }}
  >
    {!user.picture && user.nickname[0]}
  </Avatar>
);

const getNoteCardHeader = (note: ICandidateNote): string => {
  return note.userNickname ?? note.username;
};

const getNoteCardSubHeader = (note: ICandidateNote): React.ReactNode => {
  const timeSinceCreateDate = generateTimeSinceMessage(note.createDate);
  const timeSinceLastUpdate = generateTimeSinceMessage(note.lastUpdateDate);
  return note.lastUpdateDate !== note.createDate ? (
    <>
      <TimeTitle dateObj={new Date(note.createDate)}>{timeSinceCreateDate}</TimeTitle>{" "}
      <TimeTitle dateObj={new Date(note.lastUpdateDate)}>{`(edited: ${timeSinceLastUpdate})`}</TimeTitle>
    </>
  ) : (
    <TimeTitle dateObj={new Date(note.createDate)}>{timeSinceCreateDate}</TimeTitle>
  );
};

/**
 * Returns a string depending on the current time difference
 *
 * "less than an hour ago"
 * "an hour ago"
 * "# hours ago"
 * "yesterday"
 * "# days ago"
 * "5/27/2022"
 *
 * @param date - the date of the note
 * @returns - A string representing the date based on the current time difference
 */
const generateTimeSinceMessage = (date: Date): string => {
  if (!date) {
    return "";
  }
  const HOUR = 1000 * 60 * 60;
  const DAY = 1000 * 60 * 60 * 24;
  const now = new Date();
  const dateObj = new Date(date);
  const timeDifference = now.getTime() - dateObj.getTime();
  if (timeDifference < HOUR) {
    return "less than an hour ago";
  } else if (timeDifference < DAY) {
    const numberOfHours: number = Math.floor(timeDifference / HOUR);
    return numberOfHours === 1 ? "an hour ago" : `${numberOfHours} hours ago`;
  } else {
    const numberOfDays: number = Math.floor(timeDifference / DAY);
    if (numberOfDays === 1) {
      return "yesterday";
    } else if (numberOfDays < 7) {
      return `${numberOfDays} days ago`;
    } else {
      return dateObj.toLocaleDateString();
    }
  }
};

/**
 *
 * @param props.dateObj - the full date object
 * @param props.children - React children to be rendered with the title tag
 * @returns - a string representing the date for HTML title tooltips on hover
 */
const TimeTitle = ({ dateObj, children }: { dateObj: Date; children: React.ReactNode }) => {
  const dateString = dateObj.toDateString();
  const timeString = dateObj.toTimeString();
  return <span title={`${dateString} - ${timeString}`}>{children}</span>;
};
